
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { AppData, PaymentMode, ExpenseCategory, Expense, Payment, PaymentType } from '../types';
import { DollarSign, TrendingUp, Wallet, Plus, ArrowUpRight, ArrowDownLeft, Clock, Briefcase, Sparkles, AlertTriangle, Trash2, Pencil, X, RefreshCw, Lock } from 'lucide-react';
import { getFinancialSummary, detectAnomalies } from '../services/geminiService';

interface DashboardProps {
  data: AppData;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Local Delete Protection Modal
const DashboardDeleteModal: React.FC<{ isOpen: boolean; onClose: () => void; onConfirm: () => void; t: any }> = ({ isOpen, onClose, onConfirm, t }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex items-center gap-3 text-red-600 mb-4">
          <div className="bg-red-100 p-2 rounded-full"><Lock size={20} /></div>
          <h3 className="text-lg font-bold">{t.confirm} {t.delete}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">{t.password}:</p>
        <input 
            type="password" 
            placeholder={t.password}
            className="w-full border border-gray-300 p-3 rounded-lg mb-2 focus:ring-2 focus:ring-red-500 outline-none"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            autoFocus
        />
        {error && <p className="text-xs text-red-600 font-medium mb-3">{error}</p>}
        <div className="flex gap-3 mt-4">
             <button onClick={() => { onClose(); setPassword(''); }} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">{t.evt_cancel_btn}</button>
             <button onClick={() => {
                if (password.toLowerCase() === 'ganesha') {
                    onConfirm();
                    onClose();
                    setPassword('');
                } else {
                    setError('Incorrect password');
                }
             }} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">{t.confirm}</button>
        </div>
      </div>
    </div>
  );
};

export const Dashboard: React.FC<DashboardProps> = ({ data }) => {
  const { t } = data;
  // Editing State
  const [editingId, setEditingId] = useState<string | null>(null);

  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{id: string, type: 'expense' | 'income'} | null>(null);

  // Global Expense State
  const [showGlobalExpense, setShowGlobalExpense] = useState(false);
  const [newGlobalExpense, setNewGlobalExpense] = useState({
    amount: '',
    category: ExpenseCategory.MISC,
    mode: PaymentMode.CASH,
    paidBy: 'Owner',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Global Income State
  const [showGlobalIncome, setShowGlobalIncome] = useState(false);
  const [newGlobalIncome, setNewGlobalIncome] = useState({
    eventId: '',
    amount: '',
    type: PaymentType.PARTIAL,
    mode: PaymentMode.UPI,
    date: new Date().toISOString().split('T')[0]
  });

  // AI State
  const [aiSummary, setAiSummary] = useState<string>('Click refresh for AI insights...');
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const [loadingAi, setLoadingAi] = useState(false);

  const fetchAiInsights = useCallback(async (force = false) => {
    setLoadingAi(true);
    setAiSummary("Scanning database entries...");
    
    try {
        // We delay slightly to allow UI to update to "Scanning" state
        await new Promise(r => setTimeout(r, 100)); 
        
        const summary = await getFinancialSummary(data, force);
        setAiSummary(summary);
        
        const alerts = await detectAnomalies(data, force);
        setAnomalies(alerts);
    } catch (e) {
        setAiSummary("Unable to load insights (System Busy).");
    } finally {
        setLoadingAi(false);
    }
  }, [data]);

  // --- AUTO-FETCH REMOVED TO PREVENT RATE LIMIT ERRORS ---
  // Users must now click the refresh button manually.
  /*
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
        fetchAiInsights(false);
    }, 1500);
    return () => clearTimeout(debounceTimer);
  }, [fetchAiInsights]); 
  */

  // --- Handlers ---

  const handleDeleteTransaction = (e: React.MouseEvent, t: any) => {
      e.stopPropagation();
      setDeleteTarget({ id: t.id, type: t.type });
      setDeleteModalOpen(true);
  };

  const executeDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'expense') {
        data.deleteExpense(deleteTarget.id);
    } else {
        data.deletePayment(deleteTarget.id);
    }
    setDeleteTarget(null);
    // Do not auto-refresh AI here to save tokens
  };

  const openAddExpense = (categoryOverride?: ExpenseCategory) => {
    setEditingId(null);
    setNewGlobalExpense({
        amount: '',
        category: categoryOverride || ExpenseCategory.MISC,
        mode: PaymentMode.CASH,
        paidBy: 'Owner',
        description: '',
        date: new Date().toISOString().split('T')[0]
    });
    setShowGlobalExpense(true);
  };

  const openAddIncome = () => {
    setEditingId(null);
    setNewGlobalIncome({
        eventId: '',
        amount: '',
        type: PaymentType.PARTIAL,
        mode: PaymentMode.UPI,
        date: new Date().toISOString().split('T')[0]
    });
    setShowGlobalIncome(true);
  }

  const handleEditTransaction = (e: React.MouseEvent, t: any) => {
    e.stopPropagation();
    setEditingId(t.id);
    if (t.type === 'expense') {
        setNewGlobalExpense({
            amount: t.amount.toString(),
            category: t.rawCategory,
            mode: t.mode,
            paidBy: t.paidBy,
            description: t.description || '',
            date: t.date
        });
        setShowGlobalExpense(true);
    } else {
        // Income
        setNewGlobalIncome({
            eventId: t.eventId || '',
            amount: t.amount.toString(),
            type: t.rawType,
            mode: t.mode,
            date: t.date
        });
        setShowGlobalIncome(true);
    }
  };

  const handleSaveGlobalExpense = () => {
    if(Number(newGlobalExpense.amount) <= 0) return alert("Amount must be valid");
    
    if (editingId) data.deleteExpense(editingId);

    const expense: Expense = {
      id: editingId || Math.random().toString(36).substr(2, 9),
      eventId: null, // Global
      category: newGlobalExpense.category,
      amount: Number(newGlobalExpense.amount),
      mode: newGlobalExpense.mode,
      paidBy: newGlobalExpense.paidBy,
      description: newGlobalExpense.description,
      date: newGlobalExpense.date
    };
    
    data.addExpense(expense);
    setShowGlobalExpense(false);
  };

  const handleSaveGlobalIncome = () => {
    if(!newGlobalIncome.eventId) return alert("Please select an event.");
    if(Number(newGlobalIncome.amount) <= 0) return alert("Amount must be valid");

    if (editingId) data.deletePayment(editingId);

    const payment: Payment = {
        id: editingId || Math.random().toString(36).substr(2, 9),
        eventId: newGlobalIncome.eventId,
        amount: Number(newGlobalIncome.amount),
        type: newGlobalIncome.type,
        mode: newGlobalIncome.mode,
        date: newGlobalIncome.date,
        collectedBy: 'Admin' 
    };

    data.addPayment(payment);
    setShowGlobalIncome(false);
  }
  
  // --- Derived Data ---
  const stats = useMemo(() => {
    let totalRevenue = 0;
    let pendingBalance = 0;
    
    data.events.forEach(event => {
      const eventStats = data.getEventStats(event.id);
      totalRevenue += eventStats.totalPaid;
      pendingBalance += eventStats.balanceDue;
    });

    const eventExpenses = data.expenses.filter(e => e.eventId !== null).reduce((sum, e) => sum + e.amount, 0);
    const operationalExpenses = data.expenses.filter(e => e.eventId === null && e.category !== ExpenseCategory.INVESTMENT).reduce((sum, e) => sum + e.amount, 0);
    const investmentExpenses = data.expenses.filter(e => e.category === ExpenseCategory.INVESTMENT).reduce((sum, e) => sum + e.amount, 0);

    const grossProfit = totalRevenue - eventExpenses;
    const netProfit = grossProfit - operationalExpenses; 

    const cashIn = data.payments
      .filter(p => p.mode === PaymentMode.CASH)
      .reduce((sum, p) => sum + p.amount, 0);
    const cashOut = data.expenses
      .filter(e => e.mode === PaymentMode.CASH)
      .reduce((sum, e) => sum + e.amount, 0);
    const cashOnHand = cashIn - cashOut;

    return { totalRevenue, eventExpenses, operationalExpenses, investmentExpenses, grossProfit, netProfit, pendingBalance, cashOnHand };
  }, [data]);

  const recentTransactions = useMemo(() => {
    // Combine payments and expenses into a single timeline
    const p = data.payments.map(x => {
        const evt = data.events.find(e => e.id === x.eventId);
        return {
            id: x.id,
            type: 'income',
            amount: x.amount,
            label: `${x.type} - ${evt?.customerName || 'Unknown'}`,
            date: x.date,
            mode: x.mode,
            details: evt?.customerName,
            // Raw data for editing
            eventId: x.eventId,
            rawType: x.type
        };
    });
    const e = data.expenses.map(x => ({
        id: x.id,
        type: 'expense',
        amount: x.amount,
        label: `${x.category} ${x.eventId ? '(Event)' : '(Ops)'}`,
        date: x.date,
        mode: x.mode,
        details: x.description || x.paidBy,
        // Raw data for editing
        rawCategory: x.category,
        paidBy: x.paidBy,
        description: x.description
    }));

    return [...p, ...e].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 10);
  }, [data.payments, data.expenses, data.events]);

  const overdueEvents = useMemo(() => {
    return data.events
        .filter(e => e.status === 'Completed')
        .map(e => ({...e, stats: data.getEventStats(e.id)}))
        .filter(e => e.stats.balanceDue > 0);
  }, [data.events, data.getEventStats]);

  const expenseByCategory = useMemo(() => {
    const catMap: Record<string, number> = {};
    Object.values(ExpenseCategory).forEach(c => catMap[c] = 0);
    data.expenses.forEach(e => {
      catMap[e.category] = (catMap[e.category] || 0) + e.amount;
    });
    return Object.keys(catMap).map(name => ({ name, value: catMap[name] })).filter(i => i.value > 0);
  }, [data.expenses]);

  const monthlyData = useMemo(() => {
    const map: Record<string, {name: string, income: number, expense: number}> = {};
    data.payments.forEach(p => {
      const month = p.date.substring(0, 7);
      if (!map[month]) map[month] = { name: month, income: 0, expense: 0 };
      map[month].income += p.amount;
    });
    data.expenses.forEach(e => {
      const month = e.date.substring(0, 7);
      if (!map[month]) map[month] = { name: month, income: 0, expense: 0 };
      map[month].expense += e.amount;
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [data.payments, data.expenses]);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <DashboardDeleteModal 
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={executeDelete}
        t={t}
      />

      {/* Header with Smart Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
           <h2 className="text-2xl font-bold text-gray-800">{t.dash_title}</h2>
           <p className="text-gray-500 text-sm">{t.dash_subtitle}</p>
        </div>
        <div className="flex gap-3">
            <button 
            onClick={openAddIncome}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition shadow-sm font-medium"
            >
            <Plus size={18} /> {t.btn_income}
            </button>
            <button 
            onClick={() => openAddExpense()}
            className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 transition shadow-sm font-medium"
            >
            <Plus size={18} /> {t.btn_expense}
            </button>
        </div>
      </div>

      {/* AI Smart Insight Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-xl p-6 text-white shadow-lg relative overflow-hidden group">
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
                <Sparkles className="text-yellow-300 animate-pulse" size={20} />
                <h3 className="font-bold text-lg">{t.smart_insight}</h3>
            </div>
            <button 
                onClick={() => fetchAiInsights(true)}
                disabled={loadingAi}
                className={`p-2 rounded-full hover:bg-white/20 transition ${loadingAi ? 'animate-spin' : ''}`}
                title="Refresh AI Analysis"
            >
                <RefreshCw size={18} className="text-indigo-100" />
            </button>
          </div>
          <p className="text-indigo-100 leading-relaxed max-w-4xl min-h-[1.5rem] transition-opacity duration-300">
             {loadingAi ? "Analyzing..." : aiSummary}
          </p>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 bg-white opacity-5 skew-x-12 transform translate-x-12 transition-transform duration-500 group-hover:translate-x-6"></div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* ... (KPI Cards unchanged) ... */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t.kpi_gross}</p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">₹{stats.grossProfit.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg text-indigo-600">
              <DollarSign size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t.kpi_net}</p>
              <p className={`text-2xl font-bold mt-1 ${stats.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ₹{stats.netProfit.toLocaleString()}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${stats.netProfit >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
              <TrendingUp size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t.kpi_burn}</p>
              <p className="text-2xl font-bold text-purple-700 mt-1">₹{stats.operationalExpenses.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-purple-600">
              <Briefcase size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t.kpi_cash}</p>
              <p className="text-2xl font-bold text-teal-600 mt-1">₹{stats.cashOnHand.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-teal-50 rounded-lg text-teal-600">
              <Wallet size={24} />
            </div>
          </div>
        </div>
      </div>
      
      {/* 🟢 CLEAR TABLE VIEW 🟢 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
           <h3 className="text-lg font-bold text-gray-800">{t.table_title}</h3>
           <p className="text-sm text-gray-500">{t.table_subtitle}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold tracking-wider">
                <th className="px-6 py-4">{t.col_event}</th>
                <th className="px-6 py-4 text-right">{t.col_bill}</th>
                <th className="px-6 py-4 text-right">{t.col_in}</th>
                <th className="px-6 py-4 text-right">{t.col_out}</th>
                <th className="px-6 py-4 text-right">{t.col_profit}</th>
                <th className="px-6 py-4 text-center">{t.col_status}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.events.map(event => {
                const est = data.getEventStats(event.id);
                return (
                  <tr key={event.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                       <div className="font-bold text-gray-900">{event.customerName}</div>
                       <div className="text-xs text-gray-500">{event.date} • {event.eventType}</div>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                       ₹{event.finalTotal.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-green-600">
                       ₹{est.totalPaid.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-red-600">
                       ₹{est.totalExpenses.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                       <span className={`font-bold ${est.netProfit >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                         ₹{est.netProfit.toLocaleString()}
                       </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                       {est.balanceDue <= 0 ? (
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
                           Full Paid
                         </span>
                       ) : (
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800">
                           Due: ₹{est.balanceDue.toLocaleString()}
                         </span>
                       )}
                    </td>
                  </tr>
                );
              })}
              {data.events.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No events found. Start by creating an event!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Charts */}
        <div className="lg:col-span-2 space-y-6">

            {/* Monthly Chart */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-6">{t.chart_cashflow}</h3>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} dy={10} />
                        <YAxis axisLine={false} tickLine={false} />
                        <RechartsTooltip 
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        <Bar dataKey="income" fill="#10B981" name="Income" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="expense" fill="#EF4444" name="Expense" radius={[4, 4, 0, 0]} />
                    </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Expense Breakdown */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{t.chart_expense}</h3>
                <div className="h-64 flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                        data={expenseByCategory}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        >
                        {expenseByCategory.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend layout="vertical" verticalAlign="middle" align="right" />
                    </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>

        {/* Right Column: Feeds */}
        <div className="space-y-6">
            
            {/* AI Anomalies Widget */}
            {anomalies.length > 0 && (
                <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-100">
                    <h3 className="text-yellow-800 font-bold flex items-center gap-2 mb-3">
                        <AlertTriangle size={18} /> Alerts
                    </h3>
                    <div className="space-y-3">
                        {anomalies.map((alert, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-yellow-400">
                                <p className="text-sm font-bold text-gray-800">{alert.title}</p>
                                <p className="text-xs text-gray-600 mt-1">{alert.description}</p>
                                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded mt-2 inline-block ${
                                    alert.severity === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                    {alert.severity} Priority
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Investment Summary Widget */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                <h3 className="text-blue-800 font-bold flex items-center gap-2 mb-3">
                    <ArrowUpRight size={18} /> {t.ops_invest}
                </h3>
                <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
                    <div>
                        <p className="text-sm font-medium text-gray-600">Total Invested</p>
                        <p className="text-xl font-bold text-blue-700">₹{stats.investmentExpenses.toLocaleString()}</p>
                    </div>
                    <button 
                        onClick={() => openAddExpense(ExpenseCategory.INVESTMENT)}
                        className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg shadow-sm transition"
                        title="Add Investment"
                    >
                        <Plus size={18} />
                    </button>
                </div>
            </div>

            {/* Recent Transactions Feed */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-bold text-gray-800">{t.recent_tx}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                    {recentTransactions.map((t, i) => (
                        <div key={i} className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between group">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${t.type === 'income' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                    {t.type === 'income' ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900 line-clamp-1">{t.label}</p>
                                    <p className="text-xs text-gray-500">{t.date} • {t.mode}</p>
                                </div>
                            </div>
                            <div className="text-right flex items-center gap-3">
                                <span className={`text-sm font-bold ${t.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                                    {t.type === 'income' ? '+' : '-'}₹{t.amount.toLocaleString()}
                                </span>
                                <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                    <button 
                                        type="button"
                                        onClick={(e) => handleEditTransaction(e, t)}
                                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={(e) => handleDeleteTransaction(e, t)}
                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {recentTransactions.length === 0 && (
                        <div className="p-6 text-center text-gray-400 text-sm">No recent transactions</div>
                    )}
                </div>
            </div>
        </div>
      </div>

      {/* --- MODALS --- */}
      {/* (Expense and Income Modals code remains the same as previous) */}
      {showGlobalExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm transform transition-all scale-100">
             <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-900">
                    {editingId ? t.edit : t.btn_expense}
                </h3>
                <button onClick={() => setShowGlobalExpense(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                </button>
             </div>
             
             <div className="space-y-4">
               <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.category}</label>
                 <select 
                    className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                    value={newGlobalExpense.category}
                    onChange={e => setNewGlobalExpense({...newGlobalExpense, category: e.target.value as ExpenseCategory})}
                 >
                   {Object.values(ExpenseCategory).map(c => <option key={c} value={c}>{c}</option>)}
                 </select>
               </div>
               
               <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.amount}</label>
                 <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-400">₹</span>
                    <input 
                        type="number" 
                        className="w-full border border-gray-300 p-3 pl-8 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                        placeholder="0.00"
                        value={newGlobalExpense.amount}
                        onChange={e => setNewGlobalExpense({...newGlobalExpense, amount: e.target.value})} 
                    />
                 </div>
               </div>

               <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.date}</label>
                 <input 
                    type="date" 
                    className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                    value={newGlobalExpense.date}
                    onChange={e => setNewGlobalExpense({...newGlobalExpense, date: e.target.value})} 
                 />
               </div>

               <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.mode}</label>
                        <select 
                            className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50"
                            value={newGlobalExpense.mode}
                            onChange={e => setNewGlobalExpense({...newGlobalExpense, mode: e.target.value as PaymentMode})}
                        >
                        {Object.values(PaymentMode).map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.paid_by}</label>
                        <input 
                            type="text" 
                            className="w-full border border-gray-300 p-3 rounded-lg"
                            value={newGlobalExpense.paidBy}
                            onChange={e => setNewGlobalExpense({...newGlobalExpense, paidBy: e.target.value})} 
                        />
                    </div>
               </div>

               <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.desc}</label>
                 <input 
                    type="text" 
                    className="w-full border border-gray-300 p-3 rounded-lg"
                    placeholder="Optional details..."
                    value={newGlobalExpense.description}
                    onChange={e => setNewGlobalExpense({...newGlobalExpense, description: e.target.value})} 
                 />
               </div>
             </div>

             <button 
                onClick={handleSaveGlobalExpense} 
                className="w-full mt-6 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg shadow-red-200"
             >
                {editingId ? t.edit : t.btn_expense}
             </button>
          </div>
        </div>
      )}

      {showGlobalIncome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm transform transition-all scale-100">
             <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-900">
                    {editingId ? t.edit : t.btn_income}
                </h3>
                <button onClick={() => setShowGlobalIncome(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                </button>
             </div>
             
             <div className="space-y-4">
               <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.col_event}</label>
                 <select 
                    className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition"
                    value={newGlobalIncome.eventId}
                    onChange={e => setNewGlobalIncome({...newGlobalIncome, eventId: e.target.value})}
                    disabled={!!editingId} 
                 >
                   <option value="">-- Select Event --</option>
                   {data.events.map(e => (
                       <option key={e.id} value={e.id}>{e.customerName} ({e.date})</option>
                   ))}
                 </select>
               </div>
               
               <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.amount}</label>
                 <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-400">₹</span>
                    <input 
                        type="number" 
                        className="w-full border border-gray-300 p-3 pl-8 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition"
                        placeholder="0.00"
                        value={newGlobalIncome.amount}
                        onChange={e => setNewGlobalIncome({...newGlobalIncome, amount: e.target.value})} 
                    />
                 </div>
               </div>

               <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.date}</label>
                 <input 
                    type="date" 
                    className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition"
                    value={newGlobalIncome.date}
                    onChange={e => setNewGlobalIncome({...newGlobalIncome, date: e.target.value})} 
                 />
               </div>

               <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.category}</label>
                        <select 
                            className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50"
                            value={newGlobalIncome.type}
                            onChange={e => setNewGlobalIncome({...newGlobalIncome, type: e.target.value as PaymentType})}
                        >
                        {Object.values(PaymentType).map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">{t.mode}</label>
                        <select 
                            className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50"
                            value={newGlobalIncome.mode}
                            onChange={e => setNewGlobalIncome({...newGlobalIncome, mode: e.target.value as PaymentMode})}
                        >
                        {Object.values(PaymentMode).map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
               </div>
             </div>

             <button 
                onClick={handleSaveGlobalIncome} 
                className="w-full mt-6 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition shadow-lg shadow-green-200"
             >
                {editingId ? t.edit : t.btn_income}
             </button>
          </div>
        </div>
      )}

    </div>
  );
};
